/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  LitElement,
  CSSResultArray,
  TemplateResult,
  html,
} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from './nile-icon-button.css';

import '../nile-icon';
import { classMap } from 'lit/directives/class-map.js';
import { query, state } from 'lit/decorators.js';
import { literal } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { CSSResultGroup } from 'lit';
import NileElement from '../internal/nile-element';

/**
 * @summary Icons buttons are simple, icon-only buttons that can be used for actions and in toolbars.
 * @status stable
 * @since 2.0
 *
 * @dependency nile-icon
 *
 * @event nile-blur - Emitted when the icon button loses focus.
 * @event nile-focus - Emitted when the icon button gains focus.
 *
 * @csspart base - The component's base wrapper.
 */
/**
 * Nile icon component.
 *
 * @tag nile-icon-button
 *
 */
@customElement('nile-icon-button')
export class NileIconButton extends NileElement {
  static styles: CSSResultGroup = styles;

  @query('.icon-button') button: HTMLButtonElement | HTMLLinkElement;

  @state() private hasFocus = false;

  /** The name of the icon to draw. Available names depend on the icon library being used. */
  @property() name?: string;

  /** The name of a registered custom icon library. */
  @property() library?: string;

  /**
   * An external URL of an SVG file. Be sure you trust the content you are including, as it will be executed as code and
   * can result in XSS attacks.
   */
  @property() src?: string;

  /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
  @property() href?: string;

  /** Tells the browser where to open the link. Only used when `href` is set. */
  @property() target?: '_blank' | '_parent' | '_self' | '_top';

  /** Tells the browser to download the linked file as this filename. Only used when `href` is set. */
  @property() download?: string;

  /**
   * A description that gets read by assistive devices. For optimal accessibility, you should always include a label
   * that describes what the icon button does.
   */
  @property() label = '';

  /** Disables the button. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**Default color to icon */
  @property({ type: String, reflect: true }) color?: string;

  @property({ type: Boolean, reflect: true }) ishovered: boolean = false;

  private handleBlur() {
    this.hasFocus = false;
    this.emit('nile-blur');
  }

  private handleFocus() {
    this.hasFocus = true;
    this.emit('nile-focus');
  }

  private handleClick(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /** Simulates a click on the icon button. */
  click() {
    this.button.click();
  }

  /** Sets focus on the icon button. */
  focus(options?: FocusOptions) {
    this.button.focus(options);
  }

  /** Removes focus from the icon button. */
  blur() {
    this.button.blur();
  }

  private handleMouseOver(e: any) {
    this.ishovered = true;
  }
  private handleMouseLeave(e: any) {
    this.ishovered = false;
  }

  render(): TemplateResult {
    const isLink = this.href ? true : false;
    const tag = isLink ? literal`a` : literal`button`;
    /* eslint-disable lit/binding-positions, lit/no-invalid-html */
    if(isLink){
      return html`
        <a
          part="base"
          class=${classMap({
            'icon-button': true,
            'icon-button--disabled': !isLink && this.disabled,
            'icon-button--focused': this.hasFocus,
          })}
          .disabled=${isLink || this.disabled}
          type=${ifDefined(isLink ? undefined : 'button')}
          href=${ifDefined(isLink ? this.href : undefined)}
          target=${ifDefined(isLink ? this.target : undefined)}
          download=${ifDefined(isLink ? this.download : undefined)}
          rel=${ifDefined(
            isLink && this.target ? 'noreferrer noopener' : undefined
          )}
          role=${ifDefined(isLink ? undefined : 'button')}
          aria-disabled=${this.disabled ? 'true' : 'false'}
          aria-label="${this.label}"
          tabindex=${this.disabled ? '-1' : '0'}
          @blur=${this.handleBlur}
          @focus=${this.handleFocus}
          @click=${this.handleClick}
          @mouseover=${this.handleMouseOver}
          @mouseleave=${this.handleMouseLeave}
        >
      
          <nile-icon
            class="icon-button__icon"
            name=${ifDefined(this.name)}
            library=${ifDefined(this.library)}
            src=${ifDefined(this.src)}
            .color=${this.color}
            aria-hidden="true"
            size="var(--nile-icon-size-14, var(--ng-icon-size-12))"
            method="var(--nile-svg-method-fill, var(--ng-svg-method-stroke))"
          ></nile-icon>
        </a>
      `;
    }
    else{
      return html`
        <button
          part="base"
          class=${classMap({
            'icon-button': true,
            'icon-button--disabled': !isLink && this.disabled,
            'icon-button--focused': this.hasFocus,
          })}
          .disabled=${isLink || this.disabled}
          type=${ifDefined(isLink ? undefined : 'button')}
          href=${ifDefined(isLink ? this.href : undefined)}
          target=${ifDefined(isLink ? this.target : undefined)}
          download=${ifDefined(isLink ? this.download : undefined)}
          rel=${ifDefined(
            isLink && this.target ? 'noreferrer noopener' : undefined
          )}
          role=${ifDefined(isLink ? undefined : 'button')}
          aria-disabled=${this.disabled ? 'true' : 'false'}
          aria-label="${this.label}"
          tabindex=${this.disabled ? '-1' : '0'}
          @blur=${this.handleBlur}
          @focus=${this.handleFocus}
          @click=${this.handleClick}
          @mouseover=${this.handleMouseOver}
          @mouseleave=${this.handleMouseLeave}
        >
      
          <nile-icon
            class="icon-button__icon"
            name=${ifDefined(this.name)}
            library=${ifDefined(this.library)}
            src=${ifDefined(this.src)}
            .color=${this.color}
            aria-hidden="true"
            size="var(--nile-icon-size-14, var(--ng-icon-size-12))"
            method="var(--nile-svg-method-fill, var(--ng-svg-method-stroke))"
          ></nile-icon>
        </button>
      `;
    }
  }
}

export default NileIconButton;

declare global {
  interface HTMLElementTagNameMap {
    'nile-icon-button': NileIconButton;
  }
}
